import React, { useCallback } from "react";
import { breakpoints, Button, Card, Text } from "@trailhead/asurion-ui";
import styled from "styled-components";
import { useQuery } from "react-query";
import { fetchFromContentful, EntryCollection, parseIntoImage, NodeData } from "@trailhead/contentful";
import { useMxContext } from "@trailhead/mx-context";
import { trackEvent } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";

const MainContainer = styled.div``;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100vw;
`;

const CardStyled = styled(Card)`
  padding: 1px;
  min-width: 300px;
  display: flex;
  flex-direction: column;

  ${breakpoints.md}{
    min-width: inherit;
    align-self: stretch;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #F0F0F5;
  background-clip: padding-box;
  border-radius: 8px 8px 0 0;
  padding: 1rem;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 2;
  align-items: flex-start;
  align-self: stretch;
  justify-content: space-between;
  padding: 1rem;
  gap: 2rem;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  text-align: start;
  gap: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  text-align: start;
  padding: 1rem;
`;

const OutboundButton = styled(Button)``;

export type PathfinderContentCard = {
  headline: string;
  content: string;
  icon?: NodeData;
  buttonText?: string;
  buttonUrl?: string;
  partner: string[];
};

export const ContentCards = () => {
  const { client, language } = useMxContext();
  const t = useSanitizedTranslation();

  const { data, isLoading, isError } = useQuery(
    [
      "pathfinderContentCard",
      {
        content_type: "pathfinderContentCard",
        "fields.partner[in]": `all,${client}`,
        limit: 1000,
        locale: language || ""
      },
    ],
    fetchFromContentful,
    { staleTime: Infinity },
  );

  const buttonOnClick = useCallback((goToUrl: PathfinderContentCard['buttonUrl']) => {
    trackEvent("Content_Card_Button_Click", {
      goTo: goToUrl
    })
    window.open(goToUrl, "_blank");
  }, []);

  if (isLoading || isError || data.items.length === 0) {
    return <></>;
  }

  return (
    <MainContainer>
      <Text as="h2" size={4}>{t("Optimize your device")}</Text>
      <CardsContainer>
        {(data as EntryCollection<PathfinderContentCard>).items.map(({ fields }) => (
          <CardStyled type="call-to-action">
            {fields.icon && (<IconContainer>{parseIntoImage(fields.icon)}</IconContainer>)}
            <ContentContainer>
              <TextContainer>
                <Text size={3}>{fields.headline}</Text>
                <Text>{fields.content}</Text>
              </TextContainer>
            </ContentContainer>
            {fields.buttonUrl && fields.buttonText && <ButtonContainer><OutboundButton variant="outline" size="small" onClick={() => buttonOnClick(fields.buttonUrl)}>{fields.buttonText}</OutboundButton></ButtonContainer>}
          </CardStyled>
        ))}
      </CardsContainer>
    </MainContainer>
  );
};